import { site } from "@/data/site";
import { shareImage } from "@/data/shareImages";
import { reviews, reviewSummary, reviewPlatforms } from "@/data/reviews";

export const SITE_URL = "https://vakkundig-verhuizingen-transport.nl";

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "MovingCompany",
  "@id": `${SITE_URL}/#organisatie`,
  name: site.name,
  url: SITE_URL,
  hasMap: site.social.google,
  priceRange: "€€",
  currenciesAccepted: "EUR",
  telephone: "+31850601023",
  email: site.email,
  image: `${SITE_URL}/favicon.ico`,
  address: {
    "@type": "PostalAddress",
    streetAddress: site.address.street,
    postalCode: site.address.postalCode,
    addressLocality: site.address.city,
    addressCountry: "NL",
  },
  areaServed: ["Den Haag", "Zuid-Holland", "Nederland"],
  sameAs: [
    site.url,
    site.social.google,
    site.social.linkedin,
    site.social.instagram,
    site.social.facebook,
    site.social.tiktok,
    ...reviewPlatforms.map((p) => p.url),
  ],
  foundingDate: site.founded,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  ...(reviewSummary
    ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: reviewSummary.rating,
          reviewCount: reviewSummary.count,
          bestRating: 5,
        },
      }
    : {}),
  review: reviews.slice(0, 8).map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
    reviewBody: r.text,
    ...(r.date ? { datePublished: r.date } : {}),
  })),
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: site.name,
  inLanguage: "nl-NL",
  publisher: { "@id": `${SITE_URL}/#organisatie` },
};

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

export function serviceSchema({
  title,
  description,
  slug,
}: {
  title: string;
  description: string;
  slug: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    description,
    serviceType: title,
    url: `${SITE_URL}/diensten/${slug}`,
    provider: {
      "@type": "MovingCompany",
      "@id": `${SITE_URL}/#organisatie`,
      name: site.name,
      telephone: "+31850601023",
      url: SITE_URL,
      ...(reviewSummary
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: reviewSummary.rating,
              reviewCount: reviewSummary.count,
              bestRating: 5,
            },
          }
        : {}),
    },
    areaServed: ["Den Haag", "Zuid-Holland", "Nederland"],
  };
}

export function absoluteUrl(path: string) {
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/$/, "") || SITE_URL;
}

export function meta({
  title,
  description,
  path,
  type = "website",
  image,
  keywords,
}: {
  title: string;
  description: string;
  path?: string;
  type?: string;
  /** Sleutel uit shareImages, of een pad/URL naar een eigen deelafbeelding. */
  image?: string;
  /** Lokale zoekwoorden (plaats + dienst) voor deze pagina. */
  keywords?: string[];
}) {
  const url = path ? (path === "/" ? `${SITE_URL}/` : absoluteUrl(path)) : undefined;
  const imageUrl = absoluteUrl(shareImage(image));
  const keywordList = keywords?.length
    ? Array.from(new Set(keywords.map((k) => k.trim().toLowerCase()).filter(Boolean)))
    : [];
  return {
    meta: [
      { title },
      { name: "description", content: description },
      ...(keywordList.length ? [{ name: "keywords", content: keywordList.join(", ") }] : []),
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:site_name", content: site.name },
      { property: "og:locale", content: "nl_NL" },
      { property: "og:image", content: imageUrl },
      { property: "og:image:secure_url", content: imageUrl },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: `${site.name} — ${title}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: imageUrl },
      ...(url ? [{ property: "og:url", content: url }] : []),
    ],
    ...(url ? { links: [{ rel: "canonical", href: url }] } : {}),
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function regionSchema({
  city,
  slug,
  neighbourhoods = [],
  group,
}: {
  city: string;
  slug: string;
  neighbourhoods?: string[];
  group?: string;
}) {
  return {
    ...localBusinessSchema,
    "@id": `${SITE_URL}/werkgebied/${slug}`,
    url: `${SITE_URL}/werkgebied/${slug}`,
    name: `${site.name} — verhuisbedrijf ${city}`,
    description: `Verhuisbedrijf in ${city}: particuliere en zakelijke verhuizingen, seniorenverhuizing, inpakservice, verhuislift, opslag, ontruiming en transport.`,
    areaServed: [
      { "@type": "City", name: city },
      ...neighbourhoods.map((n) => ({ "@type": "Place", name: `${n}, ${city}` })),
      ...(group ? [{ "@type": "AdministrativeArea", name: group }] : []),
      { "@type": "AdministrativeArea", name: "Nederland" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Verhuisdiensten in ${city}`,
      itemListElement: [
        "Particuliere verhuizing",
        "Zakelijke verhuizing",
        "Seniorenverhuizing",
        "Inpakservice",
        "Verhuisliftservice",
        "Opslag",
        "Woningontruiming",
        "Evenementenlogistiek",
        "Transport",
      ].map((name) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: `${name} ${city}`, areaServed: city },
      })),
    },
  };
}
